import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ClipboardCheck, Info, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';
import api from '../../../../api/client';
import {
  IMPORTANCE_OPTIONS,
  categoryLabel,
  clampMinScore,
  clampWeight,
  importanceLabel,
  recommendBenchmarkRows,
  scoringModeLabel,
  statusLabel,
} from './benchmarkLabels';

const APTIVIEW_GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';
const HEADER_GRID = 'hidden sm:grid sm:grid-cols-[minmax(0,1fr)_7.5rem_5.5rem_5rem_2.25rem]';
const ROW_GRID = 'grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_7.5rem_5.5rem_5rem_2.25rem]';

const IMPORTANCE_TEXT = {
  critical: 'text-rose-600',
  high: 'text-amber-600',
  medium: 'text-slate-500',
  low: 'text-slate-400',
};

const COLUMN_HINTS = {
  importance: 'How strict this bar is as a hiring gate. Critical must meet Min or that bar fails.',
  weight: 'How much this bar counts in the overall score. Same number on every row means equal influence.',
  min: 'Lowest score (0–10) that still meets this bar. Leave blank if there is no floor.',
};

function HeaderHint({ label, hint, tooltipAlign = 'center' }) {
  const tooltipPos =
    tooltipAlign === 'end'
      ? 'right-0'
      : tooltipAlign === 'start'
        ? 'left-0'
        : 'left-1/2 -translate-x-1/2';
  return (
    <span className="inline-flex w-full items-center justify-center gap-1">
      <span>{label}</span>
      <span className="relative inline-flex group/hint">
        <Info
          tabIndex={0}
          className="h-3 w-3 cursor-help text-slate-300 outline-none hover:text-slate-500 focus:text-slate-500"
          aria-label={`${label}: ${hint}`}
        />
        <span
          role="tooltip"
          className={`pointer-events-none absolute top-full z-30 mt-1.5 hidden w-52 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-left text-[11px] font-medium normal-case tracking-normal text-slate-600 shadow-lg group-hover/hint:block group-focus-within/hint:block ${tooltipPos}`}
        >
          {hint}
        </span>
      </span>
    </span>
  );
}

function CriterionSkeleton() {
  return (
    <div className="px-5 py-4 animate-pulse">
      <div className="h-4 w-40 rounded bg-slate-100" />
      <div className="mt-2 h-3 w-56 rounded bg-slate-50" />
    </div>
  );
}

export default function BenchmarksEditor({
  rows = [],
  onChange,
  status = 'off',
  mode = 'local',
  jdId,
  jdFields,
  interviewStyle,
  approveOnSave = false,
  onApproveOnSaveChange,
  onSaveDraft,
  onApprove,
  saving = false,
  loading = false,
  compact = false,
}) {
  const [catalog, setCatalog] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [recommending, setRecommending] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('job-descriptions/benchmark-catalog/').then((res) => {
      if (!cancelled) setCatalog(res.data.categories || []);
    }).catch(() => {
      if (!cancelled) setCatalog([]);
    });
    return () => { cancelled = true; };
  }, []);

  const activeRows = useMemo(() => rows.filter((row) => row.is_active !== false), [rows]);
  const canApprove = activeRows.length >= 6 && activeRows.length <= 10;
  const usedSlugs = useMemo(() => new Set(rows.map((row) => row.slug).filter(Boolean)), [rows]);

  const updateRows = (next) => onChange(next);
  const updateRow = (index, patch) => {
    updateRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const removeRow = (index) => updateRows(rows.filter((_, i) => i !== index));

  const addFromCatalog = (item) => {
    if (usedSlugs.has(item.slug)) return;
    updateRows([
      ...rows,
      {
        definition_slug: item.slug,
        slug: item.slug,
        name: item.name,
        description: item.description,
        category: item.category,
        scoring_mode: item.scoring_mode,
        importance: item.default_importance || 'medium',
        weight: 100,
        weight_bps: 10000,
        min_score: item.default_importance === 'critical' ? 7 : null,
        evaluation_criteria: '',
        is_active: true,
        is_ai_suggested: false,
      },
    ]);
  };

  const recommend = async () => {
    setRecommending(true);
    setError('');
    setMessage('');
    try {
      const suggestions = await recommendBenchmarkRows(api, { jdId, jdFields });
      if (!suggestions?.length) {
        setError('Could not suggest criteria. Add them from the catalog instead.');
        return;
      }
      updateRows(suggestions);
      setMessage('Review weights, importance, and minimum scores, then approve.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not suggest competency criteria.');
    } finally {
      setRecommending(false);
    }
  };

  const busy = recommending || saving || loading;

  const filteredCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    return (catalog || []).map((group) => ({
      ...group,
      items: (group.items || []).filter((item) => {
        if (usedSlugs.has(item.slug)) return false;
        if (!q) return true;
        return (
          item.name.toLowerCase().includes(q) ||
          (item.description || '').toLowerCase().includes(q) ||
          categoryLabel(group.category).toLowerCase().includes(q)
        );
      }),
    })).filter((group) => group.items.length > 0);
  }, [catalog, catalogQuery, usedSlugs]);

  return (
    <div
      className={`max-w-full overflow-hidden ${compact ? 'rounded-xl border border-sky-100 bg-white p-3.5 sm:p-4 mt-3 shadow-2xs' : 'mt-5 p-4 sm:p-7'}`}
      style={compact ? undefined : {
        background: 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(135deg, rgba(14,165,233,0.35), rgba(217,70,239,0.35)) border-box',
        border: '1px solid transparent',
        borderRadius: 24,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 18px 40px rgba(14, 165, 233, 0.08)',
      }}
    >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2 min-w-0">
            <div
              className="p-1.5 rounded-lg shrink-0 text-white"
              style={{ background: APTIVIEW_GRADIENT }}
            >
              <ClipboardCheck className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-gray-900">Interview Benchmarks</h4>
              <p className="text-gray-500 text-xs mt-0.5 max-w-xl">
                The criteria every candidate for this role is measured against. Interviews stay the same until you approve.
              </p>
            </div>
          </div>
          {mode === 'persisted' ? (
            <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white border border-sky-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: status === 'ready' ? APTIVIEW_GRADIENT : status === 'draft' ? '#f59e0b' : '#94a3b8' }}
              />
              {statusLabel(status)}
            </span>
          ) : (
            <span className="shrink-0 text-[11px] font-medium text-gray-400">Optional</span>
          )}
        </div>

        {interviewStyle === 'campus_recruitment' ? (
          <p className="mt-2 text-[11px] text-gray-500">
            Campus interviews keep the usual question mix. These criteria are still used in the report.
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={recommend}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-xs disabled:opacity-50 cursor-pointer"
            style={{ background: APTIVIEW_GRADIENT }}
          >
            {recommending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Suggest with AI
          </button>
          <div className="flex flex-wrap items-center gap-1.5">
            {mode === 'persisted' ? (
              <button
                type="button"
                onClick={onSaveDraft}
                disabled={busy || !rows.length}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 cursor-pointer shadow-2xs"
              >
                Save draft
              </button>
            ) : null}
            {mode === 'persisted' ? (
              <button
                type="button"
                onClick={onApprove}
                disabled={busy || !canApprove}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40 cursor-pointer shadow-xs"
                style={{ background: canApprove ? APTIVIEW_GRADIENT : '#94a3b8' }}
              >
                <Check className="h-3.5 w-3.5" /> Approve
              </button>
            ) : (
              <label className={`ml-1 inline-flex items-center gap-2 rounded-lg px-2.5 py-1 text-xs ${canApprove ? 'text-slate-700' : 'text-slate-400'}`}>
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-sky-500 focus:ring-sky-400"
                  disabled={!canApprove}
                  checked={Boolean(approveOnSave) && canApprove}
                  onChange={(e) => onApproveOnSaveChange?.(e.target.checked)}
                />
                Approve when I save
              </label>
            )}
          </div>
        </div>

        <div className="mt-2.5">
          <span className="text-xs text-slate-500 font-normal">
            {activeRows.length} of 10 criteria
            {activeRows.length < 6 ? ' · add at least 6 to approve' : ''}
            {activeRows.length > 10 ? ' · at most 10 criteria' : ''}
          </span>
        </div>

        {error ? <p className="mt-3 text-[13px] text-rose-600">{error}</p> : null}
        {message ? <p className="mt-3 text-[13px] text-slate-500">{message}</p> : null}

        <div className="mt-5 rounded-2xl border border-slate-100 bg-white">
          <div className={`${HEADER_GRID} gap-2 border-b border-slate-100 px-5 py-2 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400`}>
            <span>Criterion</span>
            <HeaderHint label="Importance" hint={COLUMN_HINTS.importance} tooltipAlign="start" />
            <HeaderHint label="Weight" hint={COLUMN_HINTS.weight} />
            <HeaderHint label="Min" hint={COLUMN_HINTS.min} tooltipAlign="end" />
            <span />
          </div>

          {loading ? (
            <div className="divide-y divide-slate-100">
              {Array.from({ length: 6 }).map((_, i) => <CriterionSkeleton key={i} />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <p className="text-[14px] font-medium text-slate-800">No criteria yet</p>
              <p className="mt-1 text-[13px] text-slate-500">
                Suggest criteria with AI, or add from the catalog.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((row, index) => (
                <div key={`${row.slug || row.name}-${index}`} className="group px-4 py-3 sm:px-5 sm:py-3.5 hover:bg-slate-50/70">
                  <div className={`${ROW_GRID} items-center gap-2`}>
                    <div className="min-w-0">
                      <input
                        className="w-full bg-transparent text-[14px] font-medium text-slate-900 outline-none placeholder:text-slate-300"
                        value={row.name || ''}
                        onChange={(e) => updateRow(index, { name: e.target.value })}
                        placeholder="Criterion name"
                        title={row.description || undefined}
                      />
                      <div
                        className="mt-0.5 truncate text-[11px] text-slate-400"
                        title={[
                          row.description,
                          categoryLabel(row.category),
                          scoringModeLabel(row.scoring_mode),
                        ].filter(Boolean).join(' · ')}
                      >
                        {(row.description || '').trim()
                          ? (row.description || '').trim()
                          : (
                            <>
                              {categoryLabel(row.category)}
                              <span className="mx-1.5 text-slate-300">·</span>
                              {scoringModeLabel(row.scoring_mode)}
                            </>
                          )}
                        {row.is_ai_suggested ? (
                          <>
                            <span className="mx-1.5 text-slate-300">·</span>
                            Suggested
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex w-full items-center justify-center gap-1">
                      <span className="sm:hidden inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-slate-400" title={COLUMN_HINTS.importance}>
                        Importance
                        <Info className="h-2.5 w-2.5" />
                      </span>
                      <div className="relative">
                        <select
                          className={`appearance-none bg-transparent pr-4 text-center text-[13px] font-medium outline-none ${IMPORTANCE_TEXT[row.importance] || IMPORTANCE_TEXT.medium}`}
                          value={row.importance || 'medium'}
                          onChange={(e) => {
                            const importance = e.target.value;
                            const patch = { importance };
                            if (importance === 'critical' && (row.min_score == null || row.min_score === '')) {
                              patch.min_score = 7;
                            }
                            if (patch.min_score != null) {
                              patch.min_score = clampMinScore(patch.min_score);
                            }
                            updateRow(index, patch);
                          }}
                          aria-label="Importance"
                          title={COLUMN_HINTS.importance}
                        >
                          {IMPORTANCE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                      </div>
                    </div>
                    <label className="flex items-center justify-center gap-1">
                      <span className="sm:hidden inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-slate-400" title={COLUMN_HINTS.weight}>
                        Weight
                        <Info className="h-2.5 w-2.5" />
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        className="w-[3rem] bg-transparent text-center text-[13px] tabular-nums text-slate-800 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        value={row.weight ?? (row.weight_bps != null ? Math.round(Number(row.weight_bps) / 100) : '')}
                        aria-label="Weight"
                        title={COLUMN_HINTS.weight}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();
                        }}
                        onChange={(e) => {
                          const next = e.target.value === '' ? 0 : clampWeight(e.target.value);
                          updateRow(index, { weight: next, weight_bps: Math.round(next * 100) });
                        }}
                      />
                    </label>
                    <label className="flex items-center justify-center gap-1">
                      <span className="sm:hidden inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-slate-400" title={COLUMN_HINTS.min}>
                        Min
                        <Info className="h-2.5 w-2.5" />
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        placeholder="—"
                        className="w-[3.5rem] bg-transparent text-center text-[13px] tabular-nums text-slate-800 outline-none placeholder:text-slate-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        value={row.min_score ?? ''}
                        aria-label="Minimum score"
                        title={COLUMN_HINTS.min}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();
                        }}
                        onChange={(e) => updateRow(index, {
                          min_score: e.target.value === '' ? null : clampMinScore(e.target.value),
                        })}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-slate-300 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-500"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => setCatalogOpen((open) => !open)}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-slate-800"
          >
            <Plus className="h-3.5 w-3.5" />
            {catalogOpen ? 'Hide catalog' : 'Add from catalog'}
          </button>
          {catalogOpen ? (
            <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
              <input
                className="mb-3 w-full rounded-xl border-0 bg-white px-3 py-2 text-[13px] text-slate-800 shadow-sm outline-none ring-1 ring-slate-200 focus:ring-sky-300"
                placeholder="Search criteria"
                value={catalogQuery}
                onChange={(e) => setCatalogQuery(e.target.value)}
              />
              {filteredCatalog.length === 0 ? (
                <p className="px-1 py-4 text-[13px] text-slate-500">No matching criteria left to add.</p>
              ) : (
                <div className="max-h-64 space-y-4 overflow-y-auto pr-1">
                  {filteredCatalog.map((group) => (
                    <div key={group.category}>
                      <div className="mb-1.5 px-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
                        {group.label || categoryLabel(group.category)}
                      </div>
                      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {group.items.map((item) => (
                          <button
                            key={item.slug}
                            type="button"
                            onClick={() => addFromCatalog(item)}
                            className="rounded-xl bg-white px-3 py-2.5 text-left ring-1 ring-slate-100 hover:ring-sky-200"
                          >
                            <div className="text-[13px] font-medium text-slate-800">{item.name}</div>
                            {item.description ? (
                              <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">{item.description}</div>
                            ) : null}
                            <div className="mt-0.5 text-[11px] text-slate-400">
                              {scoringModeLabel(item.scoring_mode)}
                              {item.default_importance ? ` · ${importanceLabel(item.default_importance)}` : ''}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
    </div>
  );
}
